/**
 * Wallet + ghost-mode React contexts and hooks. Providers live in WalletProvider.tsx / GhostProvider.tsx.
 *
 *   useWallet()        → the connected EIP-1193 wallet (MetaMask first). Never switches chains:
 *                        signing code switches to chain 1337 at sign time.
 *   useGhost()         → watch-address ("ghost") mode state, mirrored in the URL as ?ghost=0x...
 *   useViewedAccount() → the address whose data the UI shows. Ghost overrides the wallet; canTrade only
 *                        when viewing your own connected wallet.
 */
import { createContext, useContext } from 'react';
import type { Address } from 'viem';
import type { EIP1193Provider, EIP6963ProviderDetail, EIP6963ProviderInfo, WalletStatus } from './types';
import { t } from '../i18n/i18n';

export interface WalletContextValue {
  status: WalletStatus;
  /** Checksummed address when status === 'connected'. */
  address: Address | null;
  /** Active chain id (informational; do not gate on it, signing switches chain itself). */
  chainId: number | null;
  /** EIP-1193 provider of the connected wallet (null unless connected). */
  provider: EIP1193Provider | null;
  walletInfo: EIP6963ProviderInfo | null;
  /** Discovered wallets, MetaMask first. */
  wallets: readonly EIP6963ProviderDetail[];
  /** Last user-facing error (rejected request, no wallet, ...). */
  error: string | null;
  /** Connect the wallet with this rdns (default: first discovered, i.e. MetaMask). Resolves to the address or null. */
  connect: (rdns?: string) => Promise<Address | null>;
  /** Forget the wallet locally (and revoke permissions where supported). */
  disconnect: () => void;
  /** Re-run EIP-6963 discovery. */
  refreshWallets: () => void;
  clearError: () => void;
}

export const WalletContext = createContext<WalletContextValue | null>(null);

export function useWallet(): WalletContextValue {
  const ctx = useContext(WalletContext);
  if (!ctx) throw new Error('useWallet() must be used inside <WalletProvider>');
  return ctx;
}

export interface GhostContextValue {
  /** Checksummed watch address, or null when not ghosting. */
  ghostAddress: Address | null;
  /** Set when ?ghost= held an invalid address (the param is dropped). */
  ghostError: string | null;
  /** Validates and enters ghost mode; returns false (and sets nothing) for invalid input. */
  enterGhost: (address: string) => boolean;
  exitGhost: () => void;
  clearGhostError: () => void;
}

export const GhostContext = createContext<GhostContextValue | null>(null);

export function useGhost(): GhostContextValue {
  const ctx = useContext(GhostContext);
  if (!ctx) throw new Error('useGhost() must be used inside <GhostProvider>');
  return ctx;
}

export type ViewedAccountMode = 'wallet' | 'ghost' | 'none';

export interface ViewedAccount {
  address: Address | null;
  mode: ViewedAccountMode;
  /** True only when viewing your own connected wallet. Ghost mode is read-only. */
  canTrade: boolean;
}

export function useViewedAccount(): ViewedAccount {
  const { status, address } = useWallet();
  const { ghostAddress } = useGhost();
  if (ghostAddress) return { address: ghostAddress, mode: 'ghost', canTrade: false };
  if (status === 'connected' && address) return { address, mode: 'wallet', canTrade: true };
  return { address: null, mode: 'none', canTrade: false };
}

/** Example (very active) + outcome-active addresses offered as ghost-mode quick picks. */
const trader = (address: string) => ({
  address,
  get label() {
    return t('wallet.ghost.pick_trader');
  },
});

/** Labels are getters so they follow the active language (#799). */
export const GHOST_QUICK_PICKS: readonly { address: string; readonly label: string }[] = [
  {
    address: '0xd0956a1eba0a9060baf5f73ee26b9601dc47ec43',
    get label() {
      return t('wallet.ghost.pick_spec');
    },
  },
  trader('0xc8b5b0ea995b12da364bb216652302a5161bcd85'),
  trader('0x3bf39e4677efb07a775437b5e4bd6acf12906858'),
  trader('0x73513a6ab68a8fdc1a6262a4d34962ee9af8590c'),
];
